// no-dd-sa:tsx-react/no-redundant-fragments
import { Suspense, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useOrgStore } from '@/store/orgStore';
import { useSubscriptionStore } from '@/store/subscriptionStore';
import { useThemeStore } from '@/store/themeStore';
import { analytics } from '@/lib/analytics';
import { initSentry } from '@/lib/sentry';
import { initStuckSpinnerWatchdog } from '@/lib/stuckSpinnerWatchdog';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { usePageTracking } from '@/hooks/usePageTracking';
import { useSessionHeartbeat } from '@/hooks/useSessionHeartbeat';
import ToastContainer from '@/components/common/ToastContainer';
import ScrollToTop from '@/components/common/ScrollToTop';
import TrailingSlashRedirect from '@/components/common/TrailingSlashRedirect';
import ProtectedAdminRoute from '@/components/common/ProtectedAdminRoute';
import ChunkErrorBoundary from '@/components/common/ChunkErrorBoundary';
import StuckEscapeHatch from '@/components/common/StuckEscapeHatch';
import { StuckEscapeProvider } from '@/hooks/useStuckEscapeAction';

const Layout = lazyWithRetry(() => import('@/components/layout/Layout'));
const ProfessionalLandingPage = lazyWithRetry(() => import('@/pages/ProfessionalLandingPage'));
const AuditLandingPage = lazyWithRetry(() => import('@/pages/landing/AuditLandingPage'));
const DeveloperAnalyticsPage = lazyWithRetry(() => import('@/pages/landing/DeveloperAnalyticsPage'));
const BusFactorPage = lazyWithRetry(() => import('@/pages/landing/BusFactorPage'));
const CodeHotspotsPage = lazyWithRetry(() => import('@/pages/landing/CodeHotspotsPage'));
const StoriesPage = lazyWithRetry(() => import('@/pages/StoriesPage'));
const StoryDetailPage = lazyWithRetry(() => import('@/pages/StoryDetailPage'));
const SecurityPage = lazyWithRetry(() => import('@/pages/SecurityPage'));
const AboutPage = lazyWithRetry(() => import('@/pages/AboutPage'));
const StatusPage = lazyWithRetry(() => import('@/pages/StatusPage'));
const PrivacyPage = lazyWithRetry(() => import('@/pages/PrivacyPage'));
const TermsPage = lazyWithRetry(() => import('@/pages/TermsPage'));
const DPAPage = lazyWithRetry(() => import('@/pages/DPAPage'));
const PricingPage = lazyWithRetry(() => import('@/pages/PricingPage'));
const LinearBAlternativePage = lazyWithRetry(() => import('@/pages/LinearBAlternativePage'));
const SwarmiaAlternativePage = lazyWithRetry(() => import('@/pages/SwarmiaAlternativePage'));
const JellyfishAlternativePage = lazyWithRetry(() => import('@/pages/JellyfishAlternativePage'));
const PluralsightFlowAlternativePage = lazyWithRetry(() => import('@/pages/PluralsightFlowAlternativePage'));
const AICodeReviewImpactPage = lazyWithRetry(() => import('@/pages/AICodeReviewImpactPage'));
const FiveMinuteSetupPage = lazyWithRetry(() => import('@/pages/FiveMinuteSetupPage'));
const LoginPage = lazyWithRetry(() => import('@/pages/LoginPage'));
const CallbackPage = lazyWithRetry(() => import('@/pages/CallbackPage'));
const OnboardingRouter = lazyWithRetry(() => import('@/components/onboarding/OnboardingRouter'));
const OnboardingOrgsPage = lazyWithRetry(() => import('@/pages/OnboardingOrgsPage'));
const PrerequisitesPage = lazyWithRetry(() => import('@/pages/PrerequisitesPage'));
const OnboardingOrgAccessCheckPage = lazyWithRetry(() => import('@/pages/OnboardingOrgAccessCheckPage'));
const OrganizationSelectPage = lazyWithRetry(() => import('@/pages/OrganizationSelectPage'));
const OnboardingRepoSelectPage = lazyWithRetry(() => import('@/pages/OnboardingRepoSelectPage'));
const OnboardingSyncPage = lazyWithRetry(() => import('@/pages/OnboardingSyncPage'));
const OnboardingPersonalizationPage = lazyWithRetry(() => import('@/pages/OnboardingPersonalizationPage'));
const OnboardingWowMomentPage = lazyWithRetry(() => import('@/pages/OnboardingWowMomentPage'));
const DashboardPage = lazyWithRetry(() => import('@/pages/DashboardPage'));
const OrganizationsPage = lazyWithRetry(() => import('@/pages/OrganizationsPage'));
const RepositoriesPage = lazyWithRetry(() => import('@/pages/RepositoriesPage'));
const RepositoryDetailPage = lazyWithRetry(() => import('@/pages/RepositoryDetailPage'));
const RepositoryComparePage = lazyWithRetry(() => import('@/pages/RepositoryComparePage'));
const DevelopersPage = lazyWithRetry(() => import('@/pages/DevelopersPage'));
const DeveloperDetailPage = lazyWithRetry(() => import('@/pages/DeveloperDetailPage'));
const DeveloperComparisonPage = lazyWithRetry(() => import('@/pages/DeveloperComparisonPage'));
const RepoLeaguePage = lazyWithRetry(() => import('@/pages/RepoLeaguePage'));
const TeamLeaguePage = lazyWithRetry(() => import('@/pages/TeamLeaguePage'));
const AwardsPage = lazyWithRetry(() => import('@/pages/AwardsPage'));
const TestChampionsPage = lazyWithRetry(() => import('@/pages/TestChampionsPage'));
const PolyglotLeaderboardPage = lazyWithRetry(() => import('@/pages/PolyglotLeaderboardPage'));
const PerformanceInsightsPage = lazyWithRetry(() => import('@/pages/PerformanceInsightsPage'));
const RetentionRiskPage = lazyWithRetry(() => import('@/pages/RetentionRiskPage'));
const AlertsPage = lazyWithRetry(() => import('@/pages/AlertsPage'));
const NotificationHistoryPage = lazyWithRetry(() => import('@/pages/NotificationHistoryPage'));
const HowItWorksPage = lazyWithRetry(() => import('@/pages/HowItWorksPage'));
const ReviewNetworkPage = lazyWithRetry(() => import('@/pages/ReviewNetworkPage'));
const FileHotspotsPage = lazyWithRetry(() => import('@/pages/FileHotspotsPage'));
const KnowledgeSilosPage = lazyWithRetry(() => import('@/pages/KnowledgeSilosPage'));
const CodeMaturityPage = lazyWithRetry(() => import('@/pages/CodeMaturityPage'));
const ExecutiveSummaryPage = lazyWithRetry(() => import('@/pages/ExecutiveSummaryPage'));
const RiskHealthPage = lazyWithRetry(() => import('@/pages/RiskHealthPage'));
const RiskyChangesPage = lazyWithRetry(() => import('@/pages/RiskyChangesPage'));
const PullRequestsPage = lazyWithRetry(() => import('@/pages/PullRequestsPage'));
const ReleaseReportPage = lazyWithRetry(() => import('@/pages/ReleaseReportPage'));
const DeploymentAnalyticsPage = lazyWithRetry(() => import('@/pages/DeploymentAnalyticsPage'));
const CodeReviewInsightsPage = lazyWithRetry(() => import('@/pages/CodeReviewInsightsPage'));
const BillingSettingsPage = lazyWithRetry(() => import('@/pages/BillingSettingsPage'));
const MembersPage = lazyWithRetry(() => import('@/pages/MembersPage'));
const TeamsSettingsPage = lazyWithRetry(() => import('@/pages/TeamsSettingsPage'));
const AdminLayout = lazyWithRetry(() => import('@/components/admin/AdminLayout'));
const AdminDashboardPage = lazyWithRetry(() => import('@/pages/admin/AdminDashboardPage'));
const AdminGrowthPage = lazyWithRetry(() => import('@/pages/admin/AdminGrowthPage'));
const AdminOrganizationsPage = lazyWithRetry(() => import('@/pages/admin/AdminOrganizationsPage'));
const AdminOrganizationDetailPage = lazyWithRetry(() => import('@/pages/admin/AdminOrganizationDetailPage'));
const AdminUsersPage = lazyWithRetry(() => import('@/pages/admin/AdminUsersPage'));
const AdminIssuesPage = lazyWithRetry(() => import('@/pages/admin/AdminIssuesPage'));
const AdminGiftPage = lazyWithRetry(() => import('@/pages/admin/AdminGiftPage'));
const AdminAuditLogPage = lazyWithRetry(() => import('@/pages/admin/AdminAuditLogPage'));
const AdminCancellationFeedbackPage = lazyWithRetry(() => import('@/pages/admin/AdminCancellationFeedbackPage'));
const AdminFeedbackPage = lazyWithRetry(() => import('@/pages/admin/AdminFeedbackPage'));
const AdminFeedbackDetailPage = lazyWithRetry(() => import('@/pages/admin/AdminFeedbackDetailPage'));
const AdminHealthCheckPage = lazyWithRetry(() => import('@/pages/admin/AdminHealthCheckPage'));
const AdminEmailTestsPage = lazyWithRetry(() => import('@/pages/admin/AdminEmailTestsPage'));
const AdminMarketingDashboardPage = lazyWithRetry(() => import('@/pages/admin/AdminMarketingDashboardPage'));
const AdminOnboardingPage = lazyWithRetry(() => import('@/pages/admin/AdminOnboardingPage'));
const AdminSupportInboxPage = lazyWithRetry(() => import('@/pages/admin/AdminSupportInboxPage'));
const AdminSupportConversationPage = lazyWithRetry(() => import('@/pages/admin/AdminSupportConversationPage'));
const AdminSupportKBPage = lazyWithRetry(() => import('@/pages/admin/AdminSupportKBPage'));
const AdminSupportCopilotPage = lazyWithRetry(() => import('@/pages/admin/AdminSupportCopilotPage'));
const AdminGitHubAppMigrationPage = lazyWithRetry(() => import('@/pages/admin/AdminGitHubAppMigrationPage'));
const AdminContributorActivityPage = lazyWithRetry(() => import('@/pages/admin/AdminContributorActivityPage'));
const PublicSharePage = lazyWithRetry(() => import('@/pages/PublicSharePage'));
const YearInReviewPage = lazyWithRetry(() => import('@/pages/YearInReviewPage'));
const PublicTeamProfilePage = lazyWithRetry(() => import('@/pages/PublicTeamProfilePage'));
const BenchmarkPage = lazyWithRetry(() => import('@/pages/BenchmarkPage'));
const ResourcesPage = lazyWithRetry(() => import('@/pages/ResourcesPage'));
const SPACEDashboardPage = lazyWithRetry(() => import('@/pages/SPACEDashboardPage'));
const SurveysPage = lazyWithRetry(() => import('@/pages/SurveysPage'));
const VelocityGraphPage = lazyWithRetry(() => import('@/pages/VelocityGraphPage'));
const ForecastingDashboardPage = lazyWithRetry(() => import('@/pages/ForecastingDashboardPage'));
const InvestmentAllocationPage = lazyWithRetry(() => import('@/pages/InvestmentAllocationPage'));
const AIToolsROIPage = lazyWithRetry(() => import('@/pages/AIToolsROIPage'));
const AIRoiPage = lazyWithRetry(() => import('@/pages/AIRoiPage'));
const IssueLeadTimePage = lazyWithRetry(() => import('@/pages/IssueLeadTimePage'));
const IntegrationsPage = lazyWithRetry(() => import('@/pages/IntegrationsPage'));
const AIToolConnectionsPage = lazyWithRetry(() => import('@/pages/AIToolConnectionsPage'));
const GuidesPage = lazyWithRetry(() => import('@/pages/GuidesPage'));
const GuideDetailPage = lazyWithRetry(() => import('@/pages/GuideDetailPage'));
const ToolsPage = lazyWithRetry(() => import('@/pages/ToolsPage'));
const ToolDetailPage = lazyWithRetry(() => import('@/pages/ToolDetailPage'));
const ToolEmbedPage = lazyWithRetry(() => import('@/pages/ToolEmbedPage'));
const EngineeringCodeIntelligencePage = lazyWithRetry(
  () => import('@/pages/landing/EngineeringCodeIntelligencePage')
);
const SampleReportPage = lazyWithRetry(() => import('@/pages/SampleReportPage'));
const FeaturesPage = lazyWithRetry(() => import('@/pages/FeaturesPage'));
const FeatureDetailPage = lazyWithRetry(() => import('@/pages/features/FeatureDetailPage'));
const QuizPage = lazyWithRetry(() => import('@/pages/QuizPage'));
const PlaybookPage = lazyWithRetry(() => import('@/pages/PlaybookPage'));
const BenchmarkReportPage = lazyWithRetry(() => import('@/pages/BenchmarkReportPage'));
const GitHubAnalyticsPage = lazyWithRetry(() => import('@/pages/GitHubAnalyticsPage'));
const TrialUnsubscribePage = lazyWithRetry(() => import('@/pages/TrialUnsubscribePage'));
const ContactUsPage = lazyWithRetry(() => import('@/pages/ContactUsPage'));
// Feature landing pages (public marketing)
const ForecastingLandingPage = lazyWithRetry(() => import('@/pages/ForecastingLandingPage'));
const InvestmentLandingPage = lazyWithRetry(() => import('@/pages/InvestmentLandingPage'));
const SpaceLandingPage = lazyWithRetry(() => import('@/pages/SpaceLandingPage'));
const RiskyChangesLandingPage = lazyWithRetry(() => import('@/pages/RiskyChangesLandingPage'));
const DeploymentLandingPage = lazyWithRetry(() => import('@/pages/DeploymentLandingPage'));
const AlertsLandingPage = lazyWithRetry(() => import('@/pages/AlertsLandingPage'));
const SprintRetroLandingPage = lazyWithRetry(() => import('@/pages/SprintRetroLandingPage'));
const ReviewInsightsLandingPage = lazyWithRetry(() => import('@/pages/ReviewInsightsLandingPage'));
const AIToolsLandingPage = lazyWithRetry(() => import('@/pages/AIToolsLandingPage'));
const CodeReviewStudyPage = lazyWithRetry(() => import('@/pages/research/CodeReviewStudyPage'));
const CodeReviewStudy2025Page = lazyWithRetry(() => import('@/pages/research/CodeReviewStudy2025Page'));
const ResearchHubPage = lazyWithRetry(() => import('@/pages/research/ResearchHubPage'));
const SelfMergeRealityPage = lazyWithRetry(() => import('@/pages/research/SelfMergeRealityPage'));
const RubberStampProblemPage = lazyWithRetry(() => import('@/pages/research/RubberStampProblemPage'));
const MondayMergeDayPage = lazyWithRetry(() => import('@/pages/research/MondayMergeDayPage'));
const FirstContributionTaxPage = lazyWithRetry(() => import('@/pages/research/FirstContributionTaxPage'));
const LanguageSpeedHierarchyPage = lazyWithRetry(
  () => import('@/pages/research/LanguageSpeedHierarchyPage')
);
const ThousandLineThresholdPage = lazyWithRetry(
  () => import('@/pages/research/ThousandLineThresholdPage')
);
const BotAutomationTrendsPage = lazyWithRetry(
  () => import('@/pages/research/BotAutomationTrendsPage')
);
const EliteTeamBenchmarksPage = lazyWithRetry(
  () => import('@/pages/research/EliteTeamBenchmarksPage')
);
const AlwaysOnCulturePage = lazyWithRetry(() => import('@/pages/research/AlwaysOnCulturePage'));
const AICodeReviewPrecisionPage = lazyWithRetry(
  () => import('@/pages/research/AICodeReviewPrecisionPage')
);
// 2025 Spin-off articles
const EnterpriseBenchmarkPage = lazyWithRetry(
  () => import('@/pages/research/EnterpriseBenchmarkPage')
);
const CycleTimeDecodedPage = lazyWithRetry(() => import('@/pages/research/CycleTimeDecodedPage'));
const FirstTimerPenalty2025Page = lazyWithRetry(
  () => import('@/pages/research/FirstTimerPenalty2025Page')
);
const AIDeveloperToolsPage = lazyWithRetry(() => import('@/pages/research/AIDeveloperToolsPage'));
const RubberStampProblem2025Page = lazyWithRetry(
  () => import('@/pages/research/RubberStampProblem2025Page')
);
const SelfMergeReality2025Page = lazyWithRetry(
  () => import('@/pages/research/SelfMergeReality2025Page')
);
const WednesdayMergeDayPage = lazyWithRetry(() => import('@/pages/research/WednesdayMergeDayPage'));
const LanguageSpeedHierarchy2025Page = lazyWithRetry(
  () => import('@/pages/research/LanguageSpeedHierarchy2025Page')
);
const BotCollapse2025Page = lazyWithRetry(() => import('@/pages/research/BotCollapse2025Page'));
const WeekendWorkBurnoutPage = lazyWithRetry(
  () => import('@/pages/research/WeekendWorkBurnoutPage')
);
// Thought leadership articles
const McKinseyProductivityReportPage = lazyWithRetry(
  () => import('@/pages/research/McKinseyProductivityReportPage')
);
const AnalyticsToolAcquisitionsPage = lazyWithRetry(
  () => import('@/pages/research/AnalyticsToolAcquisitionsPage')
);
const SurveyFatiguePage = lazyWithRetry(
  () => import('@/pages/research/SurveyFatiguePage')
);
const SprintRetroPage = lazyWithRetry(() => import('@/pages/SprintRetroPage'));
const GoalsPage = lazyWithRetry(() => import('@/pages/GoalsPage'));
const ShipLogPage = lazyWithRetry(() => import('@/pages/ShipLogPage'));
const InstallationCallbackPage = lazyWithRetry(() => import('@/pages/InstallationCallbackPage'));
const OnboardingInstallAppPage = lazyWithRetry(() => import('@/pages/OnboardingInstallAppPage'));
const OnboardingWrongAccountPage = lazyWithRetry(() => import('@/pages/OnboardingWrongAccountPage'));
const OnboardingAdoPage = lazyWithRetry(() => import('@/pages/OnboardingAdoPage'));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  const location = useLocation();

  // Session heartbeat for visit tracking (only when authenticated)
  useSessionHeartbeat();

  if (!isAuthenticated) {
    // Capture the current path and search params to redirect back after login
    const currentPath = location.pathname + location.search;
    const returnUrl = encodeURIComponent(currentPath);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }

  return <>{children}</>;
}

// Page tracking component - must be inside BrowserRouter
function PageTracker() {
  usePageTracking();
  return null;
}

// Preserves the query string (gclid/UTM ad-attribution params) - a bare
// <Navigate to="..."> would drop it. nginx serves the authoritative 301 with
// $is_args$args; this covers in-app history entries.
function LegacyEngineeringIntelligenceRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/engineering-code-intelligence', search: location.search }} replace />;
}

// Vanity redirects for the YouTube Shorts campaign (series-a). Unlike the legacy
// engineering-intelligence redirect, these attach a FIXED UTM query string rather
// than preserving the incoming one - the URLs are spoken/on-screen in a video and
// carry no query string of their own. nginx serves the authoritative 301; this
// covers in-app history entries (e.g. a viewer pastes the link into an already-open tab).
function PrCheckVanityRedirect() {
  return (
    <Navigate
      to="/tools/pr-size-analyzer?utm_source=youtube&utm_medium=short&utm_campaign=series-a&utm_content=pr-check"
      replace
    />
  );
}

function TeamCheckVanityRedirect() {
  return (
    <Navigate
      to="/tools/github-org-analyzer?utm_source=youtube&utm_medium=short&utm_campaign=series-a&utm_content=team-check"
      replace
    />
  );
}

function App() {
  const { isDarkMode } = useThemeStore();
  const { isAuthenticated, user, loadUser } = useAuthStore();
  const { currentOrg } = useOrgStore();
  const { subscription } = useSubscriptionStore();

  // Toggle dark class on document element
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Initialize analytics and error tracking (handles production check internally)
  useEffect(() => {
    analytics.init();
    initSentry();
    // Start the app-wide stuck-spinner watchdog (no-ops until Sentry is ready).
    initStuckSpinnerWatchdog();
  }, []);

  useEffect(() => {
    if (isAuthenticated && !user) {
      loadUser();
    }
  }, [isAuthenticated, user, loadUser]);

  useEffect(() => {
    if (!user) return;

    analytics.identify({
      userId: user.user_id,
      email: user.email,
      name: user.name,
      githubLogin: user.github_login,
      orgId: currentOrg?.id,
      orgSlug: currentOrg?.slug,
      orgName: currentOrg?.name,
      isPersonal: currentOrg?.is_personal,
      subscriptionTier: subscription?.effective_tier || 'free',
      isTrial: subscription?.is_trial_active || false,
      trialDaysRemaining: subscription?.trial_days_remaining || 0,
      signupDate: user.created_at,
      isSuperadmin: user.is_superadmin === true,
    });
  }, [
    user,
    currentOrg?.id,
    currentOrg?.slug,
    currentOrg?.name,
    currentOrg?.is_personal,
    subscription?.effective_tier,
    subscription?.is_trial_active,
    subscription?.trial_days_remaining,
  ]);

  // Dedicated status subdomain (status.codepulsehq.com) serves only the status
  // page, at any path. Host-based so the same SPA bundle powers both.
  const isStatusHost =
    typeof window !== 'undefined' && window.location.hostname.startsWith('status.');
  if (isStatusHost) {
    // BrowserRouter is required even though this branch has no routes: the
    // status page tree calls useLocation() via <SEO>, which throws outside a
    // Router and leaves the whole page blank. BrowserRouter (not MemoryRouter)
    // so SEO builds the canonical URL from the real pathname.
    return (
      <HelmetProvider>
        <BrowserRouter>
          <ChunkErrorBoundary>
            <Suspense fallback={null}>
              <StatusPage />
            </Suspense>
          </ChunkErrorBoundary>
        </BrowserRouter>
      </HelmetProvider>
    );
  }

  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <TrailingSlashRedirect />
        <PageTracker />
        <ToastContainer />
        <StuckEscapeProvider>
        <ChunkErrorBoundary>
        <Suspense fallback={null}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<ProfessionalLandingPage />} />
            <Route path="/audit" element={<AuditLandingPage />} />
            <Route path="/developer-analytics" element={<DeveloperAnalyticsPage />} />
            {/* Feature landing pages */}
            <Route path="/features/forecasting" element={<ForecastingLandingPage />} />
            <Route path="/features/investment-allocation" element={<InvestmentLandingPage />} />
            <Route path="/features/space-framework" element={<SpaceLandingPage />} />
            <Route path="/features/risky-changes" element={<RiskyChangesLandingPage />} />
            <Route path="/features/deployment-analytics" element={<DeploymentLandingPage />} />
            <Route path="/features/alerts" element={<AlertsLandingPage />} />
            <Route path="/features/sprint-retro" element={<SprintRetroLandingPage />} />
            <Route path="/features/review-insights" element={<ReviewInsightsLandingPage />} />
            <Route path="/features/ai-tools-roi" element={<AIToolsLandingPage />} />
            <Route path="/stories" element={<StoriesPage />} />
            <Route path="/stories/:slug" element={<StoryDetailPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/about" element={<AboutPage />} />
            {/* Status page is served only on the status.codepulsehq.com subdomain
                (see the host check above), not as a main-site route. */}
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/dpa" element={<DPAPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/alternative/linearb" element={<LinearBAlternativePage />} />
            <Route path="/alternative/swarmia" element={<SwarmiaAlternativePage />} />
            <Route path="/alternative/jellyfish" element={<JellyfishAlternativePage />} />
            <Route path="/alternative/pluralsight-flow" element={<PluralsightFlowAlternativePage />} />
            <Route path="/ai-code-review-impact" element={<AICodeReviewImpactPage />} />
            <Route path="/5-minute-setup" element={<FiveMinuteSetupPage />} />
            <Route path="/features/:slug" element={<FeatureDetailPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<CallbackPage />} />
            <Route
              path="/auth/installation-callback"
              element={
                <ProtectedRoute>
                  <InstallationCallbackPage />
                </ProtectedRoute>
              }
            />
            <Route path="/share/:token" element={<PublicSharePage />} />
            <Route path="/team/:slug" element={<PublicTeamProfilePage />} />
            <Route path="/guides" element={<GuidesPage />} />
            <Route path="/guides/:slug" element={<GuideDetailPage />} />
            <Route path="/tools" element={<ToolsPage />} />
            <Route path="/tools/:slug" element={<ToolDetailPage />} />
            <Route path="/tools/:slug/embed" element={<ToolEmbedPage />} />
            {/* Consolidated into /engineering-code-intelligence (near-duplicate split PMax ad traffic); nginx serves the authoritative 301 */}
            <Route path="/engineering-intelligence" element={<LegacyEngineeringIntelligenceRedirect />} />
            {/* YouTube Shorts vanity redirects (series-a campaign) to the free tools; nginx serves the authoritative 301 */}
            <Route path="/pr-check" element={<PrCheckVanityRedirect />} />
            <Route path="/team-check" element={<TeamCheckVanityRedirect />} />
            <Route path="/bus-factor" element={<BusFactorPage />} />
            <Route path="/code-hotspots" element={<CodeHotspotsPage />} />
            <Route path="/engineering-code-intelligence" element={<EngineeringCodeIntelligencePage />} />
            <Route path="/sample-report" element={<SampleReportPage />} />
            <Route path="/github-analytics" element={<GitHubAnalyticsPage />} />
            <Route path="/contact" element={<ContactUsPage />} />
            <Route path="/quiz" element={<QuizPage />} />
            <Route path="/playbook" element={<PlaybookPage />} />
            <Route path="/benchmark-report" element={<BenchmarkReportPage />} />
            <Route path="/research" element={<ResearchHubPage />} />
            <Route path="/research/code-review-study-2025" element={<CodeReviewStudy2025Page />} />
            <Route path="/research/code-review-study-2024" element={<CodeReviewStudyPage />} />
            <Route path="/research/self-merge-reality" element={<SelfMergeRealityPage />} />
            <Route path="/research/rubber-stamp-problem" element={<RubberStampProblemPage />} />
            <Route path="/research/monday-merge-day" element={<MondayMergeDayPage />} />
            <Route path="/research/first-contribution-tax" element={<FirstContributionTaxPage />} />
            <Route path="/research/language-speed-hierarchy" element={<LanguageSpeedHierarchyPage />} />
            <Route path="/research/1000-line-threshold" element={<ThousandLineThresholdPage />} />
            <Route path="/research/bot-automation-trends" element={<BotAutomationTrendsPage />} />
            <Route path="/research/elite-team-benchmarks" element={<EliteTeamBenchmarksPage />} />
            <Route path="/research/always-on-culture" element={<AlwaysOnCulturePage />} />
            <Route
              path="/research/ai-code-review-precision"
              element={<AICodeReviewPrecisionPage />}
            />
            {/* 2025 Spin-off articles */}
            <Route
              path="/research/enterprise-benchmark-reviewed-prs"
              element={<EnterpriseBenchmarkPage />}
            />
            <Route path="/research/cycle-time-decoded" element={<CycleTimeDecodedPage />} />
            <Route
              path="/research/first-timer-penalty-2025"
              element={<FirstTimerPenalty2025Page />}
            />
            <Route
              path="/research/ai-developer-tools-shipping"
              element={<AIDeveloperToolsPage />}
            />
            <Route
              path="/research/rubber-stamp-problem-2025"
              element={<RubberStampProblem2025Page />}
            />
            <Route
              path="/research/self-merge-reality-2025"
              element={<SelfMergeReality2025Page />}
            />
            <Route path="/research/wednesday-merge-day" element={<WednesdayMergeDayPage />} />
            <Route
              path="/research/language-speed-hierarchy-2025"
              element={<LanguageSpeedHierarchy2025Page />}
            />
            <Route path="/research/bot-collapse-2025" element={<BotCollapse2025Page />} />
            {/* Thought leadership articles */}
            <Route
              path="/research/mckinsey-productivity-report"
              element={<McKinseyProductivityReportPage />}
            />
            <Route
              path="/research/analytics-tool-acquisitions"
              element={<AnalyticsToolAcquisitionsPage />}
            />
            <Route
              path="/research/survey-fatigue-developer-experience"
              element={<SurveyFatiguePage />}
            />
            <Route
              path="/research/weekend-work-burnout-risk"
              element={<WeekendWorkBurnoutPage />}
            />

            {/* Trial email management */}
            <Route path="/unsubscribe/trial/:token" element={<TrialUnsubscribePage />} />

            {/* Onboarding flow routes */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <OnboardingRouter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/orgs"
              element={
                <ProtectedRoute>
                  <OnboardingOrgsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/prerequisites"
              element={
                <ProtectedRoute>
                  <PrerequisitesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/install-app"
              element={
                <ProtectedRoute>
                  <OnboardingInstallAppPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/wrong-account"
              element={
                <ProtectedRoute>
                  <OnboardingWrongAccountPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/ado"
              element={
                <ProtectedRoute>
                  <OnboardingAdoPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/check-access"
              element={
                <ProtectedRoute>
                  <OnboardingOrgAccessCheckPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/select"
              element={
                <ProtectedRoute>
                  <OrganizationSelectPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/select-repos"
              element={
                <ProtectedRoute>
                  <OnboardingRepoSelectPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/syncing"
              element={
                <ProtectedRoute>
                  <OnboardingSyncPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/personalize"
              element={
                <ProtectedRoute>
                  <OnboardingPersonalizationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/insights"
              element={
                <ProtectedRoute>
                  <OnboardingWowMomentPage />
                </ProtectedRoute>
              }
            />

            {/* Protected routes with Layout */}
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/pull-requests" element={<PullRequestsPage />} />
              <Route path="/release-report" element={<ReleaseReportPage />} />
              <Route path="/deployment-analytics" element={<DeploymentAnalyticsPage />} />
              <Route path="/repositories" element={<RepositoriesPage />} />
              <Route path="/repositories/compare" element={<RepositoryComparePage />} />
              <Route path="/repositories/:id" element={<RepositoryDetailPage />} />
              <Route path="/developers" element={<DevelopersPage />} />
              <Route path="/developers/:id" element={<DeveloperDetailPage />} />
              <Route path="/developer-league" element={<DeveloperComparisonPage />} />
              <Route path="/repo-league" element={<RepoLeaguePage />} />
              <Route path="/team-league" element={<TeamLeaguePage />} />
              <Route path="/awards" element={<AwardsPage />} />
              <Route path="/test-champions" element={<TestChampionsPage />} />
              <Route path="/polyglot-leaderboard" element={<PolyglotLeaderboardPage />} />
              <Route path="/performance-insights" element={<PerformanceInsightsPage />} />
              <Route path="/retention-risk" element={<RetentionRiskPage />} />
              <Route path="/alerts" element={<AlertsPage />} />
              <Route path="/notification-history" element={<NotificationHistoryPage />} />
              <Route path="/review-network" element={<ReviewNetworkPage />} />
              <Route path="/file-hotspots" element={<FileHotspotsPage />} />
              <Route path="/knowledge-silos" element={<KnowledgeSilosPage />} />
              <Route path="/code-maturity" element={<CodeMaturityPage />} />
              <Route path="/risky-changes" element={<RiskyChangesPage />} />
              <Route path="/risk" element={<RiskHealthPage />} />
              <Route path="/review-insights" element={<CodeReviewInsightsPage />} />
              <Route path="/executive" element={<ExecutiveSummaryPage />} />
              <Route path="/team" element={<MembersPage />} />
              <Route path="/year-in-review" element={<YearInReviewPage />} />
              <Route path="/benchmarks" element={<BenchmarkPage />} />
              <Route path="/resources" element={<ResourcesPage />} />
              <Route path="/space" element={<SPACEDashboardPage />} />
              <Route path="/surveys" element={<SurveysPage />} />
              <Route path="/velocity" element={<VelocityGraphPage />} />
              <Route path="/forecasting" element={<ForecastingDashboardPage />} />
              <Route path="/investment" element={<InvestmentAllocationPage />} />
              <Route path="/ai-tools-roi" element={<AIToolsROIPage />} />
              <Route path="/ai-roi" element={<AIRoiPage />} />
              <Route path="/issue-lead-time" element={<IssueLeadTimePage />} />
              <Route path="/integrations" element={<IntegrationsPage />} />
<Route path="/ai-tool-usage" element={<AIToolConnectionsPage />} />
              <Route path="/integrations/linear/callback" element={<IntegrationsPage />} />
              <Route path="/sprint-retro" element={<SprintRetroPage />} />
              <Route path="/goals" element={<GoalsPage />} />
              <Route path="/ship-log" element={<ShipLogPage />} />
              <Route path="/ship-log/:entryId" element={<ShipLogPage />} />

              <Route path="/team-settings" element={<TeamsSettingsPage />} />

              {/* Admin-only routes (requires org admin role) */}
              <Route element={<ProtectedAdminRoute />}>
                <Route path="/organizations" element={<OrganizationsPage />} />
                <Route path="/billing" element={<BillingSettingsPage />} />
              </Route>
            </Route>

            {/* Admin routes - protected by AdminLayout */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboardPage />} />
              <Route path="growth" element={<AdminGrowthPage />} />
              <Route path="onboarding" element={<AdminOnboardingPage />} />
              <Route path="organizations" element={<AdminOrganizationsPage />} />
              <Route path="organizations/:id" element={<AdminOrganizationDetailPage />} />
              <Route path="users" element={<AdminUsersPage />} />
              <Route path="issues" element={<AdminIssuesPage />} />
              <Route path="gift" element={<AdminGiftPage />} />
              <Route path="audit-log" element={<AdminAuditLogPage />} />
              <Route path="cancellation-feedback" element={<AdminCancellationFeedbackPage />} />
              <Route path="feedback" element={<AdminFeedbackPage />} />
              <Route path="feedback/:feedbackId" element={<AdminFeedbackDetailPage />} />
              <Route path="health" element={<AdminHealthCheckPage />} />
              <Route path="email-tests" element={<AdminEmailTestsPage />} />
              <Route path="onboarding" element={<AdminOnboardingPage />} />
              <Route path="marketing" element={<AdminMarketingDashboardPage />} />
              <Route path="support" element={<AdminSupportInboxPage />} />
              <Route path="support/copilot" element={<AdminSupportCopilotPage />} />
              <Route path="support/kb" element={<AdminSupportKBPage />} />
              <Route path="support/:conversationId" element={<AdminSupportConversationPage />} />
              <Route path="github-migration" element={<AdminGitHubAppMigrationPage />} />
              <Route path="contributor-activity" element={<AdminContributorActivityPage />} />
            </Route>

            {/* Catch-all redirect to landing page */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        </ChunkErrorBoundary>
        <StuckEscapeHatch />
        </StuckEscapeProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
